import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, Sparkles, Check, ShieldCheck, Loader2, Users, Gift, Copy, ArrowUp, Share2, PartyPopper, Bug } from 'lucide-react';
import { useReferral, buildReferralLink } from './useReferral';
import { joinWaitlist, type WaitlistJoinResult } from './waitlistApi';
import { trackWaitlistModalOpen, trackWaitlistSubmitted, trackWaitlistCompleted, trackWaitlistError } from '../lib/analytics';
// Pre-computed particle vectors for the success-state confetti burst, so the
// pieces fan out evenly in all directions from the checkmark.
const CONFETTI_COLORS = ['#EA580C', '#F59E0B', '#22C55E', '#2B211A', '#FB923C'];
const CONFETTI = Array.from({
  length: 14
}, (_, i) => {
  const angle = i / 14 * Math.PI * 2;
  const dist = 30 + i % 3 * 8;
  return {
    x: Math.cos(angle) * dist,
    y: Math.sin(angle) * dist,
    r: (i % 2 === 0 ? 1 : -1) * (90 + i % 4 * 30),
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]
  };
});
type VideoKind = 'youtube' | 'vimeo' | 'loom' | 'file' | null;
// Detect how to embed whatever URL Tony drops in, so we render the right
// player without him having to think about it.
function getVideoEmbed(url?: string): {
  kind: VideoKind;
  src: string;
} | null {
  if (!url) return null;
  const u = url.trim();
  if (!u) return null;
  // YouTube (watch, youtu.be, or already-embed)
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return {
    kind: 'youtube',
    src: `https://www.youtube.com/embed/${yt[1]}`
  };
  // Loom
  const loom = u.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return {
    kind: 'loom',
    src: `https://www.loom.com/embed/${loom[1]}`
  };
  // Vimeo
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return {
    kind: 'vimeo',
    src: `https://player.vimeo.com/video/${vimeo[1]}`
  };
  // Direct video file
  if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(u)) return {
    kind: 'file',
    src: u
  };
  // Fallback: assume it's already an embeddable URL
  return {
    kind: 'youtube',
    src: u
  };
}
export function WaitlistModal({
  onClose,
  videoUrl,
  // The code of whoever referred this visitor (read from ?ref= / the capture
  // route). Drives the "you were invited" banner and, when wired to a backend,
  // the signup attribution.
  referredBy
}: {
  onClose: () => void;
  videoUrl?: string;
  referredBy?: string | null;
}) {
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState('');
  const [keepPosted, setKeepPosted] = useState(true);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [copied, setCopied] = useState(false);
  // The server's answer to the join. The success view ONLY renders once this is
  // set — i.e. only after a successful request — so the position/code/referral
  // data shown is always real, never a placeholder.
  const [serverData, setServerData] = useState<WaitlistJoinResult | null>(null);
  // Inline submit error (e.g. invalid email, rate limit, or the API being
  // unreachable). When set, we stay on the form and surface it to the user.
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const embed = getVideoEmbed(videoUrl);
  const isDone = status === 'done';
  // The visitor's own shareable invite link + who (if anyone) referred them.
  // ⚠️ BACKEND TODO: replace the generated code with the one returned by
  // POST /waitlist when the real API exists (see components/useReferral.ts).
  const {
    referralLink,
    referredBy: storedReferrer
  } = useReferral();
  const inviter = (referredBy ?? storedReferrer)?.trim() || null;

  // Track when the modal opens
  useEffect(() => {
    trackWaitlistModalOpen(referredBy);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Success-state scenario ──────────────────────────────────────────────
  // The success view has three flavors. With a real backend these are decided
  // server-side (dedupe by email, referral attribution). For now a DEV-only
  // debug chip (bottom-left) cycles through them so the team can preview each:
  //   • 'new'       — fresh signup → celebration + "You're on the list."
  //   • 'returning' — same email recognized → "You're already on the list."
  //   • 'referred'  — someone joined via your link → bump + you moved up.
  // ⚠️ BACKEND TODO: replace `demoScenario` with the state returned by the
  // waitlist API (isExisting, referralsJoined, position).
  type Scenario = 'new' | 'returning' | 'referred';
  const [demoScenario, setDemoScenario] = useState<Scenario>('new');
  // How many friends have joined through this person's link (drives the jump).
  // Server-provided when the API answered; local stand-in otherwise.
  const referralsJoined = serverData ? serverData.referralCount : demoScenario === 'referred' ? 1 : 0;
  const SPOTS_PER_REFERRAL = 25;
  const REFERRALS_FOR_FRONT = 3;
  // The line is genuinely MOVING: as Giga onboards people each hour, everyone
  // still waiting drifts toward the front. This is the "changes every hour"
  // mechanic — a returning visitor sees they've climbed since they joined,
  // which rewards coming back WITHOUT rewarding spammy re-submits. We
  // deliberately do NOT bump on email re-entry (that's gameable); honest
  // time-based drift is.
  // ⚠️ BACKEND TODO: position = basePosition − floor(hoursSinceJoin × driftPerHour),
  // computed from a stored joinedAt timestamp. The value below is an
  // illustrative stand-in so the returning state is demoable without elapsed
  // time.
  const basePosition = useMemo(() => 140 + Math.floor(Math.random() * 80), []);
  // Local stand-in for "spots gained since you joined" (used only when the API
  // is unavailable). Deterministic so it doesn't flicker.
  const localMovedUp = 8 + basePosition % 11; // 8–18 spots
  // Local-only improvement for the demo scenarios.
  const localImproved = demoScenario === 'referred' ? referralsJoined * SPOTS_PER_REFERRAL : demoScenario === 'returning' ? localMovedUp : 0;
  // Resolved queue numbers — server values when the API answered, else the
  // local demo stand-ins. `startPosition` is where the count animates FROM
  // (the position before the recent climb); `targetPosition` is the current
  // spot it lands on.
  const startPosition = serverData ? serverData.previousPosition ?? serverData.position : basePosition;
  const targetPosition = serverData ? serverData.position : Math.max(1, basePosition - localImproved);
  const spotsImproved = Math.max(0, startPosition - targetPosition);
  // How many spots a returning visitor has drifted up since they joined.
  const movedUpSinceJoin = serverData ? spotsImproved : localMovedUp;
  // The number actually rendered in the chip — animates DOWN from basePosition
  // to targetPosition on the 'referred' scenario so the jump is felt, not just
  // stated. Snaps instantly for the other scenarios / reduced motion.
  const [displayPosition, setDisplayPosition] = useState(basePosition);
  useEffect(() => {
    if (!isDone) return;
    // Snap when there's nothing to celebrate (fresh join) or motion is reduced;
    // otherwise count the number DOWN from startPosition to the improved spot
    // so the climb is felt — both for a referral bump and for a returning
    // visitor who's drifted forward since they joined.
    if (spotsImproved <= 0 || reduceMotion) {
      setDisplayPosition(targetPosition);
      return;
    }
    setDisplayPosition(startPosition);
    let raf = 0;
    const dur = 1000;
    let startTime = 0;
    const tick = (now: number) => {
      if (!startTime) startTime = now;
      const t = Math.min(1, (now - startTime) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayPosition(Math.round(startPosition - eased * (startPosition - targetPosition)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    const id = window.setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, 350);
    return () => {
      window.clearTimeout(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isDone, spotsImproved, startPosition, targetPosition, reduceMotion]);
  // Copy that swaps with the scenario.
  const successCopy: Record<Scenario, {
    title: string;
    sub: string;
  }> = {
    new: {
      title: "You're on the list.",
      sub: "We email people in the order they joined — we'll be in touch the moment your spot opens up."
    },
    returning: {
      title: "You're already on the list.",
      sub: "Good to see you again — the line's been moving. You've climbed since you joined, and your spot keeps improving every hour."
    },
    referred: {
      title: 'You just moved up.',
      sub: 'Someone joined through your link. Keep sharing to climb closer to the front.'
    }
  };
  // Celebrate on a fresh join or a fresh bump; stay calm on a returning visit.
  const showConfetti = demoScenario !== 'returning';
  // DEV-only: cycle form → new → returning → referred → back to form so the
  // team can eyeball every state without a backend. Safe to delete with the
  // debug chip below once the real API drives these states.
  const cycleDebugState = () => {
    if (status !== 'done') {
      setStatus('done');
      setDemoScenario('new');
      return;
    }
    if (demoScenario === 'new') setDemoScenario('returning');else if (demoScenario === 'returning') setDemoScenario('referred');else {
      setDemoScenario('new');
      setStatus('idle');
    }
  };
  const debugLabel = status !== 'done' ? 'form' : demoScenario;
  // The link the visitor copies/shares. Prefer the backend-issued code (stable,
  // server-attributed); fall back to the locally generated link before the API
  // is live.
  const effectiveLink = serverData?.referralLink ? serverData.referralLink : serverData?.code ? buildReferralLink(serverData.code) : referralLink;
  // Pretty link (no protocol) for display; full link is used for copy/share.
  const displayLink = effectiveLink.replace(/^https?:\/\//, '');
  const shareText = 'I just joined the Giga waitlist — grab your spot:';
  const handleShare = async () => {
    // Prefer the native share sheet on supported devices; fall back to copy.
    if (typeof navigator !== 'undefined' && (navigator as any).share) {
      try {
        await (navigator as any).share({
          title: 'Giga',
          text: shareText,
          url: effectiveLink
        });
        return;
      } catch {

        /* user dismissed — fall through to copy */}
    }
    handleCopy();
  };
  const handleCopy = () => {
    const write = navigator?.clipboard?.writeText?.(effectiveLink);
    Promise.resolve(write).finally(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== 'idle') return;
    setErrorMsg(null);
    setStatus('loading');
    // Track the submission attempt
    trackWaitlistSubmitted(email);
    try {
      // POST the join to the real backend. The response decides the success
      // state (new / returning / referred), the queue position, and the
      // referral code — see docs/waitlist-api-spec.md for the contract.
      const result = await joinWaitlist({
        email,
        referredBy: inviter,
        keepPosted,
        source: 'marketing-site'
      });
      // ONLY advance to the success view on a confirmed, successful response —
      // and render it from this real data.
      setServerData(result);
      setDemoScenario(result.status);
      setStatus('done');
      // Track the successful signup
      trackWaitlistCompleted(result.status, result.position);
    } catch (err) {
      // The request failed (invalid email, rate limit, or the API being
      // unreachable). Do NOT proceed to "You're on the list" — keep the user on
      // the form and tell them what happened so they can retry.
      setStatus('idle');
      const msg = (err as Error)?.message || "We couldn't add you just now — please try again.";
      setErrorMsg(msg);
      // Track the error
      trackWaitlistError(msg);
    }
  };
  return <motion.div className="fixed inset-0 z-[120] flex items-stretch sm:items-center justify-center sm:p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.12,
    ease: 'easeOut'
  }} data-id="element-3367">
      
      {/* Backdrop — deeper, with a warm radial vignette so the card reads as
                                         floating in a softly lit room rather than over a flat scrim. */}
      <div className="absolute inset-0 bg-[#140F0B]/85 backdrop-blur-md" onClick={onClose} aria-hidden="true" data-id="element-3368" />
      
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
      background: 'radial-gradient(120% 90% at 50% 38%, rgba(234,88,12,0.10) 0%, rgba(20,15,11,0) 55%)'
    }} data-id="element-3369" />
      

      <motion.div role="dialog" aria-modal="true" aria-label="Join the Giga waitlist" className="group/modal relative w-full h-full max-h-none sm:h-auto sm:max-w-2xl sm:max-h-[90dvh] overflow-auto overscroll-contain rounded-none sm:rounded-[28px] bg-[#FBF9F5] text-[#2B211A] ring-0 sm:ring-1 sm:ring-black/5 shadow-none sm:shadow-[0_2px_8px_rgba(20,15,11,0.06),0_40px_80px_-24px_rgba(20,15,11,0.55)]" initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.98,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.98,
      y: 8
    }} transition={reduceMotion ? {
      duration: 0.12
    } : {
      type: 'spring',
      stiffness: 420,
      damping: 32,
      mass: 0.7
    }} data-id="element-3370">
        
        {/* Premium surface detail: a bright hairline along the top edge and a
             whisper of film grain across the whole card, so it reads like a
             crafted physical surface instead of a flat panel. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" data-id="element-3371" />
        
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-none sm:rounded-[28px] opacity-[0.025] mix-blend-multiply" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
      }} data-id="element-3372" />
        

        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/[0.04] ring-1 ring-black/5 hover:bg-black/[0.08] text-[#6B5D52] hover:text-[#2B211A] hover:rotate-90 transition-all duration-300" data-id="element-3373">
          
          <X size={18} data-id="element-3374" />
        </button>

        {/* DEV-ONLY preview chip — cycles the modal through every state
          (form → on the list → already on the list → referral bump) so the
          team can eyeball each one without a backend. HIDDEN for now per user
          request; bring it back later by removing the {false && …} guard. The
          cycleDebugState handler + scenario state it drives are preserved. */}
        {false && <button type="button" onClick={cycleDebugState} aria-label="Preview next state (dev only)" title="Dev preview — cycle waitlist states" className="absolute bottom-3 left-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#C9B8A6] bg-white/70 px-2.5 py-1 text-[10px] font-mono font-semibold uppercase tracking-wide text-[#8A7A6B] backdrop-blur-sm hover:bg-white hover:text-[#2B211A] transition-colors" data-id="element-3375">
          
            <Bug size={11} data-id="element-3376" />
            dev: {debugLabel}
          </button>}

        <div className="relative p-5 sm:p-7 md:p-9" data-id="element-3377">
          <AnimatePresence mode="wait" data-id="element-3378">
            {isDone ?
          // ── SUCCESS VIEW ──────────────────────────────────────────────
          // A focused, self-contained celebration that REPLACES the form +
          // video so the content always fits the card (no clipping). This
          // is the referral hub: queue position + share-to-skip-the-line.
          <motion.div key="success" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25
          }} className="flex flex-col items-center text-center" data-id="element-3379">
              
                {/* Celebration mark with confetti burst */}
                <span className="relative flex items-center justify-center w-16 h-16 mb-5" data-id="element-3380">
                  {!reduceMotion && showConfetti && <span className="pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-3381">
                  
                      {CONFETTI.map((c, i) => <motion.span key={i} className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-[1px]" style={{
                  backgroundColor: c.color
                }} initial={{
                  opacity: 0,
                  scale: 0,
                  x: 0,
                  y: 0
                }} animate={{
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1, 1, 0.6],
                  x: c.x,
                  y: c.y,
                  rotate: c.r
                }} transition={{
                  duration: 0.9,
                  delay: 0.12,
                  ease: [0.16, 1, 0.3, 1]
                }} data-id="element-3382" />)}
                    </span>}
                  <motion.span className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-b from-[#F97316] to-[#EA580C] ring-1 ring-white/20 shadow-[0_8px_24px_rgba(234,88,12,0.5)]" initial={reduceMotion ? {
                opacity: 0
              } : {
                scale: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={reduceMotion ? {
                duration: 0.2
              } : {
                type: 'spring',
                stiffness: 500,
                damping: 18,
                delay: 0.05
              }} data-id="element-3383">
                  
                    <Check size={28} className="text-white" strokeWidth={3} data-id="element-3384" />
                  </motion.span>
                </span>

                <h2 className="text-2xl md:text-3xl font-title font-bold tracking-tight leading-tight mb-2" data-id="element-3385">
                  {successCopy[demoScenario].title}
                </h2>
                <p className="text-sm md:text-base text-[#6B5D52] leading-relaxed max-w-md mb-6" data-id="element-3386">
                  {successCopy[demoScenario].sub}
                </p>

                {/* Bump banner — only when a referral has just landed. We never
                 name the person who joined; just that someone did. */}
                {demoScenario === 'referred' && <motion.div initial={reduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              delay: 0.15
            }} className="flex items-center gap-2.5 rounded-2xl bg-[#16A34A]/[0.08] ring-1 ring-[#16A34A]/20 px-4 py-3 mb-6" data-id="element-3387">
                
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#16A34A]/15 text-[#16A34A] shrink-0" data-id="element-3388">
                      <PartyPopper size={16} data-id="element-3389" />
                    </span>
                    <p className="text-sm font-semibold text-[#15803D] text-left leading-snug" data-id="element-3390">
                      Someone joined through your link — you jumped{' '}
                      {SPOTS_PER_REFERRAL} spots.
                    </p>
                  </motion.div>}

                {/* Queue position chip — sized up so the spot number reads as
                 the hero stat of the success state, not a quiet footnote. When
                 the visitor has improved (a referral bump, or drift since they
                 joined) it counts DOWN from the old number to the new. */}
                <div className={`inline-flex items-center gap-2.5 rounded-full bg-white px-6 py-3.5 shadow-[0_4px_16px_-6px_rgba(20,15,11,0.12)] ring-1 ${spotsImproved > 0 ? 'ring-[#16A34A]/25' : 'ring-[#EFE9E1]'} ${demoScenario === 'returning' ? 'mb-3' : 'mb-7'}`} data-id="element-3391">
                
                  {spotsImproved > 0 ? <ArrowUp size={20} className="text-[#16A34A]" data-id="element-3392" /> : <Users size={20} className="text-[#EA580C]" data-id="element-3393" />}
                  <span className="text-lg md:text-xl font-bold text-[#2B211A]" data-id="element-3394">
                    You're{' '}
                    <span className={spotsImproved > 0 ? 'text-[#16A34A] tabular-nums' : 'text-[#EA580C] tabular-nums'} data-id="element-3395">
                    
                      #{displayPosition.toLocaleString()}
                    </span>{' '}
                    in line
                  </span>
                </div>

                {/* Returning visitor: surface the honest, time-based movement —
                 they've climbed since they joined, and the line keeps moving
                 every hour. No confetti here; this is a calm "welcome back". */}
                {demoScenario === 'returning' && <div className="flex flex-col items-center gap-1 mb-7" data-id="element-3396">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-[#16A34A]" data-id="element-3397">
                      <ArrowUp size={14} strokeWidth={2.5} data-id="element-3398" />
                      You've moved up {movedUpSinceJoin} spots since you joined
                    </p>
                    <p className="flex items-center gap-1.5 text-xs font-medium text-[#9C8E82]" data-id="element-3399">
                      <span className="relative flex h-1.5 w-1.5" data-id="element-3400">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A]/60" data-id="element-3401" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#16A34A]" data-id="element-3402" />
                      </span>
                      The line keeps moving — your spot updates every hour
                    </p>
                  </div>}

                {/* Skip-the-line referral hub */}
                <div className="w-full rounded-2xl bg-white ring-1 ring-[#EFE9E1] shadow-[0_8px_24px_-8px_rgba(20,15,11,0.12)] p-6 md:p-8 text-center relative overflow-hidden" data-id="element-3403">
                  {/* Subtle premium glow at the top edge */}
                  <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#EA580C]/20 to-transparent" data-id="element-3404" />

                  <div className="flex flex-col items-center mb-6" data-id="element-3405">
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FAF8F4] ring-1 ring-[#EFE9E1] mb-4 shadow-sm" data-id="element-3406">
                      <Gift size={18} className="text-[#EA580C]" data-id="element-3407" />
                    </span>
                    <h3 className="text-lg font-bold text-[#2B211A] leading-tight mb-1.5" data-id="element-3408">
                      Priority Access
                    </h3>
                    <p className="text-sm text-[#6B5D52] leading-relaxed max-w-[260px]" data-id="element-3409">
                      Share your invite link. Each friend who joins earns you $25 worth of credits (max 3 per person).
                    </p>
                  </div>

                  {/* Invite link + copy */}
                  <div className="flex flex-col sm:flex-row items-stretch gap-2 max-w-sm mx-auto" data-id="element-3410">
                    <div className="flex-1 flex items-center justify-center sm:justify-start gap-2 rounded-xl bg-[#FAF8F4] ring-1 ring-[#EFE9E1] px-4 py-3 min-w-0 shadow-[inset_0_1px_2px_rgba(20,15,11,0.02)]" data-id="element-3411">
                      <span className="text-sm text-[#2B211A] truncate font-medium" data-id="element-3412">
                        {displayLink}
                      </span>
                    </div>
                    <button type="button" onClick={handleCopy} className="flex items-center justify-center gap-2 rounded-xl bg-[#2B211A] px-6 py-3 text-sm font-semibold text-white whitespace-nowrap transition-all hover:bg-[#1a1410] hover:shadow-md active:scale-[0.98]" data-id="element-3413">
                    
                      {copied ? <>
                          <Check size={16} strokeWidth={2.5} className="text-[#22C55E]" data-id="element-3414" />
                      
                          Copied
                        </> : <>
                          <Copy size={16} data-id="element-3415" />
                          Copy Link
                        </>}
                    </button>
                  </div>

                  {/* Referral progress — how many of the friends needed to
                   reach the front have joined so far, shown as a 3-segment
                   meter. Fills as referrals land. */}
                  <div className="mt-6 max-w-sm mx-auto text-left" data-id="element-3416">
                    <div className="flex items-center justify-between mb-2" data-id="element-3417">
                      <span className="text-xs font-semibold text-[#6B5D52]" data-id="element-3418">
                        {referralsJoined} of {REFERRALS_FOR_FRONT} friends
                        joined
                      </span>
                      {referralsJoined > 0 && <span className="text-xs font-semibold text-[#16A34A]" data-id="element-3419">
                          +{referralsJoined * SPOTS_PER_REFERRAL} spots
                        </span>}
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-3420">
                      {Array.from({
                    length: REFERRALS_FOR_FRONT
                  }).map((_, i) => <span key={i} className="relative flex-1 h-1.5 rounded-full bg-[#EFE9E1] overflow-hidden" data-id="element-3421">
                      
                          <motion.span className="absolute inset-0 rounded-full bg-[#EA580C]" initial={{
                      scaleX: 0
                    }} animate={{
                      scaleX: i < referralsJoined ? 1 : 0
                    }} transition={{
                      duration: reduceMotion ? 0 : 0.45,
                      delay: reduceMotion ? 0 : 0.3 + i * 0.12,
                      ease: 'easeOut'
                    }} style={{
                      transformOrigin: 'left'
                    }} data-id="element-3422" />
                      
                        </span>)}
                    </div>
                  </div>
                </div>
              </motion.div> :
          // ── FORM VIEW ─────────────────────────────────────────────────
          <motion.div key="form" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-3423">
              
                {/* Eyebrow — set in a soft tinted pill so the label feels like a
                 badge rather than loose text. */}
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#EA580C]/[0.08] ring-1 ring-[#EA580C]/15 px-3 py-1 text-[11px] font-semibold uppercase text-[#C2410C] mb-4 [letter-spacing:4px]" data-id="element-3424">
                  <Sparkles size={12} data-id="element-3425" />
                  Early access
                </div>

                {/* "You were invited" banner — shown when the visitor arrives
                 via a referral link, so the referral loop is visible on the
                 receiving end too. */}
                {inviter && <div className="flex items-center gap-2 rounded-xl bg-white ring-1 ring-[#EFE9E1] px-3.5 py-2.5 mb-4 shadow-[0_1px_2px_rgba(20,15,11,0.05)]" data-id="element-3426">
                    <Gift size={15} className="text-[#EA580C] shrink-0" data-id="element-3427" />
                    <p className="text-sm text-[#2B211A] font-medium" data-id="element-3428">
                      You've been invited to skip ahead — join below to claim
                      your spot.
                    </p>
                  </div>}

                {/* Heading — extra right padding on mobile so the long title
                 never tucks under the close button. */}
                <h2 className="text-2xl md:text-3xl font-title font-bold tracking-tight leading-tight mb-2 pr-8 sm:pr-0" data-id="element-3429">
                  Giga is invite-only — for now.
                </h2>
                <p className="text-sm md:text-base text-[#6B5D52] leading-relaxed max-w-xl mb-4" data-id="element-3430">
                  We're letting people in a few at a time. Grab your spot on the waitlist — we'll email
                  you the moment you're in.
                </p>

                {/* Truthful scarcity cue — no fabricated counts, just the honest
                 cadence: we onboard a limited number of teams each week. */}
                <div className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-[#EFE9E1] px-3.5 py-1.5 text-xs font-semibold text-[#6B5D52] mb-5 md:mb-6 shadow-[0_1px_2px_rgba(20,15,11,0.05)]" data-id="element-3431">
                  <Users size={13} className="text-[#EA580C]" data-id="element-3432" />
                  Onboarding a limited number of people each week
                </div>

                {/* Product walkthrough — only rendered when a real video URL is
                 passed. Tony: pass videoUrl (YouTube/Loom/Vimeo/.mp4) to
                 <WaitlistModal videoUrl="..." />. With no video we skip the
                 player entirely and go straight to the form, keeping the card
                 light and clean. */}
                {embed && <div className="aspect-video w-full rounded-2xl overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_-18px_rgba(20,15,11,0.55)] bg-[#241814] mb-6 md:mb-7" data-id="element-3433">
                    {embed.kind === 'file' ? <video src={embed.src} controls playsInline className="w-full h-full object-cover" data-id="element-3434" /> : <iframe src={embed.src} title="Giga product walkthrough" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3435" />}
                  </div>}

                {/* Waitlist form */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-id="element-3436">
                  <div className="flex flex-col sm:flex-row items-stretch gap-2.5" data-id="element-3437">
                    <input type="email" required value={email} onChange={e => {
                  setEmail(e.target.value);
                  if (errorMsg) setErrorMsg(null);
                }} aria-invalid={!!errorMsg} placeholder="you@company.com" className={`flex-1 px-4 py-3 rounded-xl bg-white ring-1 border-0 text-[#2B211A] placeholder:text-[#9C8E82] shadow-[inset_0_1px_2px_rgba(20,15,11,0.04)] focus:outline-none focus:ring-2 transition-shadow ${errorMsg ? 'ring-[#DC2626]/60 focus:ring-[#DC2626]/45' : 'ring-[#E7E0D7] focus:ring-[#EA580C]/45'}`} data-id="element-3438" />
                  
                    <button type="submit" disabled={status === 'loading'} className="relative overflow-hidden flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-b from-[#F97316] to-[#EA580C] text-white font-bold whitespace-nowrap shadow-[0_8px_20px_-6px_rgba(234,88,12,0.6)] ring-1 ring-[#C2410C]/40 transition-all duration-200 hover:shadow-[0_10px_26px_-6px_rgba(234,88,12,0.7)] hover:-translate-y-px active:translate-y-0 disabled:opacity-80 disabled:hover:translate-y-0 [&>span.sheen]:hover:translate-x-[180%]" data-id="element-3439">
                    
                      <span aria-hidden="true" className="sheen pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/25 blur-md -translate-x-[120%] transition-transform duration-700 ease-out" data-id="element-3440" />
                    
                      {status === 'loading' ? <>
                          <Loader2 size={16} className="animate-spin" data-id="element-3441" />
                          Joining…
                        </> : 'Join the waitlist'}
                    </button>
                  </div>

                  {/* Submit error — keeps the user on the form (no false
                   "you're on the list") and explains what to do. */}
                  {errorMsg && <p role="alert" className="flex items-start gap-1.5 text-sm font-medium text-[#B91C1C]" data-id="element-3442">
                  
                      <X size={14} className="mt-0.5 shrink-0" strokeWidth={2.5} data-id="element-3443" />
                  
                      {errorMsg}
                    </p>}

                  <label className="flex items-center gap-2 text-sm text-[#6B5D52] cursor-pointer select-none" data-id="element-3444">
                    <input type="checkbox" checked={keepPosted} onChange={e => setKeepPosted(e.target.checked)} className="w-4 h-4 rounded border-[#D8CEC2] text-[#EA580C] focus:ring-[#EA580C]/30 accent-[#EA580C]" data-id="element-3445" />
                  
                    Keep me posted on product news
                  </label>

                  <p className="flex items-center gap-1.5 text-xs text-[#9C8E82]" data-id="element-3446">
                    <ShieldCheck size={12} data-id="element-3447" />
                    No spam. We'll only email about your spot.
                  </p>
                </form>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>;
}